import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, MessageCircle, Phone, Instagram } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { openWhatsApp } from "@/lib/api";

export default function FloatingActions() {
  const { settings } = useSite();
  const [open, setOpen] = useState(false);
  const floating = settings?.floating || {};
  const contact = settings?.contact || {};

  const actions = [
    floating.whatsapp?.enabled && {
      id: "whatsapp",
      icon: MessageCircle,
      label: "WhatsApp",
      href: `https://wa.me/${String(floating.whatsapp.value || contact.whatsapp || "").replace(/\D/g, "")}`,
      external: true,
      onClick: () => openWhatsApp(floating.whatsapp.value || contact.whatsapp, settings?.templates?.general || "Hi!", { trackPath: "/floating-menu" }),
    },
    floating.call?.enabled && {
      id: "call",
      icon: Phone,
      label: floating.call.label || "Call",
      href: `tel:${String(floating.call.value || contact.phone || "").replace(/\s/g, "")}`,
      external: false,
      onClick: () => {},
    },
    floating.instagram?.enabled && floating.instagram.value && {
      id: "instagram",
      icon: Instagram,
      label: floating.instagram.label || "Instagram",
      href: floating.instagram.value,
      external: true,
      onClick: () => {},
    },
    floating.messenger?.enabled && floating.messenger.value && {
      id: "messenger",
      icon: MessageCircle,
      label: floating.messenger.label || "Messenger",
      href: floating.messenger.value,
      external: true,
      onClick: () => {},
    },
  ].filter(Boolean);

  if (actions.length === 0) return null;

  return (
    <div className="fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-[90] flex flex-col items-end gap-3" data-testid="floating-action-menu">
      <AnimatePresence>
        {open &&
          actions.map((a, i) => (
            <motion.a
              key={a.id}
              href={a.href}
              target={a.external ? "_blank" : undefined}
              rel="noopener noreferrer"
              onClick={(e) => a.onClick?.(e)}
              initial={{ opacity: 0, y: 16, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.8 }}
              transition={{ duration: 0.22, delay: open ? i * 0.05 : 0 }}
              className="glass-strong flex items-center gap-2.5 pl-3 pr-4 py-2.5 rounded-full text-sm font-medium text-white hover:border-accent/60 hover:shadow-[0_0_28px_rgba(124,58,237,0.4)] transition-all"
              data-testid={`floating-action-${a.id}`}
            >
              <a.icon className="h-4 w-4 text-accent-bright" />
              <span>{a.label}</span>
            </motion.a>
          ))}
      </AnimatePresence>
      <motion.button
        onClick={() => setOpen((v) => !v)}
        whileTap={{ scale: 0.9 }}
        className="h-14 w-14 rounded-full bg-accent hover:bg-accent-bright text-white flex items-center justify-center shadow-[0_0_32px_rgba(124,58,237,0.55)] transition-colors"
        aria-label={open ? "Close quick actions" : "Open quick actions"}
        data-testid="floating-action-toggle"
      >
        <motion.span animate={{ rotate: open ? 135 : 0 }} transition={{ duration: 0.3 }}>
          <Plus className="h-6 w-6" />
        </motion.span>
      </motion.button>
    </div>
  );
}
